/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  forwardRef
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from '@generated/base-types';
import {
  CategoryFragment,
  DimensionFragment
} from '@new/admin/services/load-dimensions.generated';
import { DimensionsState } from '@new/admin/state/pool-details/dimensions/dimensions.state';
import { AggregateLocalisationPipe } from '@new/common/pipes/aggregate-localisation.pipe';
import { LocalizeContentPipe } from '@new/common/pipes/localize-content.pipe';

import { SearchInputComponent } from '@new/common/search-input/search-input.component';
import { Store } from '@ngxs/store';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';

@Component({
  selector: 'man-dimension-categories-table',
  templateUrl: './dimension-categories-table.component.html',
  host: { class: 'd-flex flex-column h-100' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    SearchInputComponent,
    CheckboxModule,
    FormsModule,
    LocalizeContentPipe,
    AggregateLocalisationPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DimensionTableComponent)
    }
  ]
})
export class DimensionTableComponent {
  @Input() public subject: Subject | undefined;

  public selectedCategories: string[] = [];
  public value?: string[];
  public touched = false;
  public disabled = false;
  public onChange: (value: string[]) => void;
  public onTouched: () => void;

  public dimensions$: Observable<DimensionFragment[]>;
  public categories$: Observable<CategoryFragment[]>;
  public filteredCategories$: Observable<CategoryFragment[]>;

  public searchStringSource = new BehaviorSubject<string>('');
  public searchString$ = this.searchStringSource.asObservable();

  constructor(private store: Store) {
    this.searchStringSource.next('');
    this.dimensions$ = this.store
      .select(DimensionsState.dimensions)
      .pipe(map(dimensionsReq => dimensionsReq.data || []));

    this.categories$ = this.dimensions$.pipe(
      map(dimensions => {
        const categories: Set<CategoryFragment> = new Set();
        dimensions.forEach(dimension => {
          dimension.categories.forEach(category => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { categories: _, ...rest } = dimension;
            categories.add({
              ...category,
              dimension: { ...rest }
            });
          });
        });

        return [...categories];
      })
    );

    this.filteredCategories$ = combineLatest([
      this.categories$,
      this.searchString$
    ]).pipe(
      map(([categories, searchString]) => {
        if (!searchString) {
          return categories;
        }

        return categories.filter(category => {
          const { nameEn, nameDe, nameFr, nameIt } = category;

          return (
            (nameEn || '').toLowerCase().includes(searchString.toLowerCase()) ||
            (nameDe || '').toLowerCase().includes(searchString.toLowerCase()) ||
            (nameFr || '').toLowerCase().includes(searchString.toLowerCase()) ||
            (nameIt || '').toLowerCase().includes(searchString.toLowerCase())
          );
        });
      })
    );
  }

  public writeValue(value?: string[]): void {
    this.value = value;
    this.selectedCategories = value || [];
  }

  public registerOnChange(onChange: (value: string[]) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public onSelectionChange(): void {
    this.onChange(this.selectedCategories);
  }

  public onSearch(event: string): void {
    this.searchStringSource.next(event);
  }

  public onSearchClear(): void {
    this.searchStringSource.next('');
  }
}
